import {toMediumDate, toMediumDateTime} from '@pinecast/common/dates';
import EmptyState from '@pinecast/common/EmptyState';
import Group from '@pinecast/common/Group';
import {formatCurrency} from '@pinecast/common/helpers/format';
import {useIsMobile} from '@pinecast/common/hooks/useIsMobile';
import {ShareExternal} from '@pinecast/common/icons';
import Link from '@pinecast/common/Link';
import ListView, {ListViewItem} from '@pinecast/common/ListView';
import {Table, TableBodyCell, TableHeaderCell} from '@pinecast/common/Table';
import TooltipContainer from '@pinecast/common/TooltipContainer';
import type {RecurringDonationSupplemental_tipJarSubscription_events} from '@pinecast/gql/__generated_tj__/RecurringDonationSupplemental';
import type {SubscriptionsPage_tipJarTipEvents} from '@pinecast/gql/__generated_tj__/SubscriptionsPage';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';

const messages = defineMessages({
  colPodcast: {
    id: 'tj-spa.ReceiptList.colPodcast',
    description: 'Column header for podcast name',
    defaultMessage: 'Podcast',
  },
  colAmount: {
    id: 'tj-spa.ReceiptList.colAmount',
    description: 'Column header for donation amount',
    defaultMessage: 'Amount',
  },
  colTimestamp: {
    id: 'tj-spa.ReceiptList.colTimestamp',
    description: 'Column header for donation time',
    defaultMessage: 'When',
  },
  colReceipt: {
    id: 'tj-spa.ReceiptList.colReceipt',
    description: 'Column header for donation receipt link',
    defaultMessage: 'Receipt',
  },

  emptyEvents: {
    id: 'tj=spa.ReceiptList.emptyEvents',
    description: 'Generic empty state for tip jar receipts',
    defaultMessage: "You haven't subscribed to any podcasts yet.",
  },

  emptyEventsCopy: {
    id: 'tj=spa.ReceiptList.emptyEvents.copy',
    description: 'Generic empty state copy for tip jar receipts',
    defaultMessage:
      "After you've paid to subscribe to your first podcast, we'll list all of your receipts here.",
  },

  receiptLink: {
    id: 'tj=spa.ReceiptList.receiptLink',
    description: 'Link to a receipt',
    defaultMessage: 'View',
  },
});

type Props = {
  events: ReadonlyArray<
    | SubscriptionsPage_tipJarTipEvents
    | RecurringDonationSupplemental_tipJarSubscription_events
  >;
};

const SubscriptionsPageReceiptList = ({events}: Props) => {
  const isMobile = useIsMobile();

  if (!events.length) {
    return (
      <EmptyState
        title={<FormattedMessage {...messages.emptyEvents} />}
        copy={<FormattedMessage {...messages.emptyEventsCopy} />}
      />
    );
  }

  return isMobile ? (
    <ListView>
      {events.map(tje => {
        return (
          <ListViewItem
            forceHardNavigation
            href={`/receipt/${tje.id}`}
            primary={tje.podcast.name}
            obvious={toMediumDate(tje.occurredAt)}
            tabular={formatCurrency(tje.amount, tje.currency)}
          />
        );
      })}
    </ListView>
  ) : (
    <Table style={{marginBottom: 0}}>
      <thead>
        <tr>
          <TableHeaderCell>
            <FormattedMessage {...messages.colPodcast} />
          </TableHeaderCell>
          <TableHeaderCell>
            <FormattedMessage {...messages.colTimestamp} />
          </TableHeaderCell>
          <TableHeaderCell style={{textAlign: 'right'}}>
            <FormattedMessage {...messages.colAmount} />
          </TableHeaderCell>
          <TableHeaderCell style={{textAlign: 'right'}}>
            <FormattedMessage {...messages.colReceipt} />
          </TableHeaderCell>
        </tr>
      </thead>
      <tbody>
        {events.map(tje => {
          return (
            <tr key={tje.id}>
              <TableBodyCell>
                <Link href={`/jar/${tje.podcast.slug}`}>
                  {tje.podcast.name}
                </Link>
              </TableBodyCell>
              <TableBodyCell>
                <TooltipContainer
                  tooltipContent={toMediumDateTime(tje.occurredAt)}
                >
                  {toMediumDate(tje.occurredAt)}
                </TooltipContainer>
              </TableBodyCell>
              <TableBodyCell
                style={{
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCurrency(tje.amount, tje.currency)}
              </TableBodyCell>
              <TableBodyCell style={{textAlign: 'right'}}>
                <Link href={`/receipt/${tje.id}`} target="_blank">
                  <Group spacing={4} centerChildren>
                    <FormattedMessage {...messages.receiptLink} />
                    <ShareExternal size={14} />
                  </Group>
                </Link>
              </TableBodyCell>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default SubscriptionsPageReceiptList;
